import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "../utils/axios";

// Types based on your service DTOs
export interface MerchantPayment {
  id: string;
  orderId: string;
  toWalletAddress: string;
  amount: number;
  currency: string;
  status: PaymentStatus;
  creationTime: string;
  customerEmail: string;
  webhookUrl: string;
  description?: string;
  metadata?: Record<string, any>;
  confirmations: number;
  transactionHash?: string;
  authorized: boolean;
  dateAuthorized?: string;
  confirmationTime?: string;
}

export enum PaymentStatus {
  Pending = 0,
  Processing = 1,
  Completed = 2,
  Failed = 3,
  Expired = 4
}

export interface CreatePaymentDto {
  orderId: string;
  amount: number;
  currency: string;
  customerEmail: string;
  webhookUrl?: string;
  description?: string;
  metadata?: Record<string, any>;
  expiryMinutes?: number;
}

export interface PaymentResponseDto {
  paymentId: string;
  paymentAddress: string;
  amount: number;
  currency: string;
  expiresAt: string;
  qrCode: string;
}

export interface PaymentStatusDto {
  orderId: string;
  status: PaymentStatus;
  amount: number;
  confirmations: number;
  transactionHash?: string;
}

export interface GetPaymentsInput {
  status?: PaymentStatus;
  orderId?: string;
  customerEmail?: string;
  transactionHash?: string;
  walletAddress?: string;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  currency?: string;
  searchTerm?: string;
  includeRefunded?: boolean;
  includeExpired?: boolean;
  dateRangePreset?: DateRangePreset;
  groupBy?: GroupByPeriod;
  exportFormat?: ExportFormat;
  needsAttention?: boolean;
  skipCount: number;
  maxResultCount: number;
  sorting?: string;
}

export enum DateRangePreset {
  Today = 0,
  Yesterday = 1,
  Last7Days = 2,
  Last30Days = 3,
  ThisMonth = 4,
  LastMonth = 5,
  ThisYear = 6,
  Custom = 7
}

export enum GroupByPeriod {
  Hour = 0,
  Day = 1,
  Week = 2,
  Month = 3,
  Quarter = 4,
  Year = 5
}

export enum ExportFormat {
  Csv = 0,
  Excel = 1,
  Pdf = 2,
  Json = 3
}

export interface PaymentStatsDto {
  startDate: string;
  endDate: string;
  totalPayments: number;
  successfulPayments: number;
  failedPayments: number;
  pendingPayments: number;
  processingPayments: number;
  expiredPayments: number;
  totalVolumeByCurrency: Record<string, number>;
  paymentsByStatus: Record<PaymentStatus, number>;
  uniqueCustomers: number;
  averageAmountByCurrency: Record<string, number>;
  averageConfirmationTimeMinutes: number;
}

interface MerchantPaymentState {
  payments: MerchantPayment[];
  selectedPayment: MerchantPayment | null;
  currentPaymentResponse: PaymentResponseDto | null;
  paymentStatus: PaymentStatusDto | null;
  stats: PaymentStatsDto | null;
  loading: boolean;
  error: { message: string; code: string } | null;
  totalCount: number;
}

const initialState: MerchantPaymentState = {
  payments: [],
  selectedPayment: null,
  currentPaymentResponse: null,
  paymentStatus: null,
  stats: null,
  loading: false,
  error: null,
  totalCount: 0,
};

// Helper function to convert object to query params
const toQueryParams = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, value.toString());
    }
  });
  return searchParams.toString();
};

// Async Thunks

export const createPaymentRequest = createAsyncThunk(
  "merchantPayment/createPaymentRequest",
  async (payload: CreatePaymentDto, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "api/services/app/MerchantPayment/CreatePaymentRequest",
        payload
      );
      return response.data.result as PaymentResponseDto;
    } catch (error: any) {
      return rejectWithValue({
        message: error.response?.data?.error?.message || "Failed to create payment request",
        code: "CREATE_PAYMENT_FAILED",
      });
    }
  }
);

export const getPaymentStatus = createAsyncThunk(
  "merchantPayment/getPaymentStatus",
  async (orderId: string, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `api/services/app/MerchantPayment/GetPaymentStatus`,
        { params: { orderId } }
      );
      return response.data.result as PaymentStatusDto;
    } catch (error: any) {
      return rejectWithValue({
        message: error.response?.data?.error?.message || "Failed to get payment status",
        code: "GET_PAYMENT_STATUS_FAILED",
      });
    }
  }
);

export const getMerchantPayments = createAsyncThunk(
  "merchantPayment/getMerchantPayments",
  async (input: GetPaymentsInput, { rejectWithValue }) => {
    try {
      // Use GET with query parameters instead of POST
      const queryString = toQueryParams(input);
      const response = await axios.get(
        `api/services/app/MerchantPayment/GetMerchantPayments${queryString ? `?${queryString}` : ""}`
      );
      return {
        items: response.data.result as MerchantPayment[],
        totalCount: response.data.totalCount || response.data.result?.length || 0,
      };
    } catch (error: any) {
      return rejectWithValue({
        message: error.response?.data?.error?.message || "Failed to fetch merchant payments",
        code: "FETCH_PAYMENTS_FAILED",
      });
    }
  }
);

export const getPaymentStats = createAsyncThunk(
  "merchantPayment/getPaymentStats",
  async (payload: { startDate: string; endDate: string }, { rejectWithValue }) => {
    try {
      const { startDate, endDate } = payload;
      const response = await axios.get(
        "api/services/app/MerchantPayment/GetPaymentStats",
        { params: { startDate, endDate } }
      );
      return response.data.result as PaymentStatsDto;
    } catch (error: any) {
      return rejectWithValue({
        message: error.response?.data?.error?.message || "Failed to fetch payment stats",
        code: "FETCH_STATS_FAILED",
      });
    }
  }
);

export const authorizePayment = createAsyncThunk(
  "merchantPayment/authorizePayment",
  async (paymentId: string, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `api/services/app/MerchantPayment/AuthorizePaymentAscync`,
        null,
        { params: { paymentId } }
      );
      return { paymentId, success: response.data.result as boolean };
    } catch (error: any) {
      return rejectWithValue({
        message: error.response?.data?.error?.message || "Failed to authorize payment",
        code: "AUTHORIZE_PAYMENT_FAILED",
      });
    }
  }
);

// Slice
const merchantPaymentSlice = createSlice({
  name: "merchantPayment",
  initialState,
  reducers: {
    setSelectedPayment: (state, action: PayloadAction<MerchantPayment>) => {
      state.selectedPayment = action.payload;
    },
    clearSelectedPayment: (state) => {
      state.selectedPayment = null;
    },
    clearCurrentPaymentResponse: (state) => {
      state.currentPaymentResponse = null;
    },
    clearPaymentStatus: (state) => {
      state.paymentStatus = null;
    },
    resetMerchantPaymentState: () => initialState,
    updatePaymentLocally: (state, action: PayloadAction<{ id: string; updates: Partial<MerchantPayment> }>) => {
      const index = state.payments.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.payments[index] = { ...state.payments[index], ...action.payload.updates };
      }
      if (state.selectedPayment?.id === action.payload.id) {
        state.selectedPayment = { ...state.selectedPayment, ...action.payload.updates };
      }
    },
    setPayments: (state, action: PayloadAction<MerchantPayment[]>) => {
      state.payments = action.payload;
    },
    addPayment: (state, action: PayloadAction<MerchantPayment>) => {
      state.payments.unshift(action.payload);
    },
    removePayment: (state, action: PayloadAction<string>) => {
      state.payments = state.payments.filter(payment => payment.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Payment Request
      .addCase(createPaymentRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPaymentRequest.fulfilled, (state, action) => {
        state.currentPaymentResponse = action.payload;
        state.loading = false;
      })
      .addCase(createPaymentRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as { message: string; code: string };
      })

      // Get Payment Status
      .addCase(getPaymentStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPaymentStatus.fulfilled, (state, action) => {
        state.paymentStatus = action.payload;
        state.loading = false;
      })
      .addCase(getPaymentStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as { message: string; code: string };
      })

      // Get Merchant Payments
      .addCase(getMerchantPayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMerchantPayments.fulfilled, (state, action) => {
        state.payments = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.loading = false;
      })
      .addCase(getMerchantPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as { message: string; code: string };
      })

      // Get Payment Stats
      .addCase(getPaymentStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPaymentStats.fulfilled, (state, action) => {
        state.stats = action.payload;
        state.loading = false;
      })
      .addCase(getPaymentStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as { message: string; code: string };
      })

      // Authorize Payment
      .addCase(authorizePayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(authorizePayment.fulfilled, (state, action) => {
        // Update the payment status locally if it exists in the list
        const paymentIndex = state.payments.findIndex(p => p.id === action.payload.paymentId);
        if (paymentIndex !== -1) {
          state.payments[paymentIndex] = {
            ...state.payments[paymentIndex],
            authorized: true,
            dateAuthorized: new Date().toISOString(),
            status: PaymentStatus.Completed,
          };
        }
        if (state.selectedPayment?.id === action.payload.paymentId) {
          state.selectedPayment = {
            ...state.selectedPayment,
            authorized: true,
            dateAuthorized: new Date().toISOString(),
            status: PaymentStatus.Completed,
          };
        }
        if (state.paymentStatus) {
          state.paymentStatus = {
            ...state.paymentStatus,
            status: PaymentStatus.Completed,
          };
        }
        state.loading = false;
      })
      .addCase(authorizePayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as { message: string; code: string };
      });
  },
});

export const {
  setSelectedPayment,
  clearSelectedPayment,
  clearCurrentPaymentResponse,
  clearPaymentStatus,
  resetMerchantPaymentState,
  updatePaymentLocally,
  setPayments,
  addPayment,
  removePayment,
} = merchantPaymentSlice.actions;

export default merchantPaymentSlice.reducer;